'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';
import { useTheme } from '@/app/components/theme-provider';
import { useLanguage } from '@/app/context/language-context';
import dynamic from 'next/dynamic';
import Slider from 'react-slick';
import { Check, ChevronLeft } from 'lucide-react';
import AboutSeo from '../components/seo/about-seo';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Link from 'next/link';
import { Languages, ChevronRight } from 'lucide-react';
import { authService } from '../../services/auth.service';

// Add the dynamic import for the Map component (no SSR)
const Map = dynamic(() => import('@/app/components/about/map'), { ssr: false });

export default function AboutUs() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { scrollY } = useScroll();
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('All');
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const pathname = usePathname();
  const router = useRouter();
  const visionSectionRef = useRef(null);

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await authService.getAgents();

      if (response?.data?.items?.length) {
        const apiData = response.data;

        const filteredReraAgents = apiData.items.filter(
          (agent) => agent.is_rera === true,
        );

        setAgents(filteredReraAgents);
        setFilteredAgents(filteredReraAgents);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Pass the current page to the Navbar component
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const navbarElement = document.querySelector('header');
      if (navbarElement) {
        // This is a hack to pass the current page to the Navbar component
        // since we can't directly modify the Navbar props from here
        navbarElement.setAttribute('data-page', 'about');
      }
    }
  }, [pathname]);

  // Parallax effects
  const y1 = useTransform(scrollY, [0, 1000], [0, -150]);
  const y2 = useTransform(scrollY, [0, 1000], [0, -100]);
  const opacity1 = useTransform(scrollY, [0, 300], [1, 0.5]);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const handleLanguageClick = (language) => {
    setSelectedLanguage(language);

    if (language === 'All') {
      setFilteredAgents(agents);
    } else {
      setFilteredAgents(
        agents.filter((agent) => agent.languages?.includes(language)),
      );
    }
  };

  // Add a function to handle scrolling to the message form
  const scrollToVissionSection = () => {
    // Use setTimeout to ensure the contact section is rendered before scrolling
    setTimeout(() => {
      if (visionSectionRef.current) {
        // Scroll to the element with an offset to show the heading
        const yOffset = -120; // Adjust this value to control how much of the top part is visible
        const element = visionSectionRef.current;
        const y =
          element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const uniqueLanguages = [
    'All', // Always include 'All' first
    ...Array.from(
      new Set(
        agents
          .flatMap((agent) => agent.languages || []) // Flatten all languages arrays
          .filter(Boolean), // Remove any undefined/null
      ),
    ),
  ];

  const dynamicSettings = {
    dots: false, // ❌ disable slick dots
    arrows: false, // ❌ hide default slick arrows
    speed: 500,
    slidesToScroll: 1,
    autoplay: filteredAgents.length > 1,
    autoplaySpeed: 3000,
    infinite: filteredAgents.length > 4,
    slidesToShow: Math.min(4, filteredAgents.length),
    centerMode: filteredAgents.length < 2,
    centerPadding: '20px',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <>
      <AboutSeo />
      <div className='bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden'>
        {/* Hero Section */}
        <section className='relative min-h-screen flex items-center justify-center overflow-hidden pt-20'>
          {/* Background with animated gradient */}
          <div className='absolute inset-0 bg-gradient-to-b from-gray-100 to-white dark:from-gray-900 dark:to-gray-950 z-0'>
            <motion.div
              className='absolute inset-0 opacity-30 dark:opacity-40'
              animate={{
                background: [
                  'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                  'radial-gradient(circle at 80% 70%, rgba(56, 245, 239, 0.15) 0%, rgba(254, 42, 83, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                  'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                ],
              }}
              transition={{
                duration: 15,
                repeat: Number.POSITIVE_INFINITY,
                ease: 'easeInOut',
              }}
            />
          </div>

          {/* Decorative elements */}
          <div className='absolute inset-0 overflow-hidden z-0'>
            {/* Top left circle */}
            <motion.div
              className='absolute -top-20 -left-20 w-96 h-96 rounded-full bg-primarycolor/5 dark:bg-primarycolor/10'
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 10, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 20,
                ease: 'easeInOut',
              }}
            />

            {/* Bottom right circle */}
            <motion.div
              className='absolute -bottom-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-secondarycolor/5 dark:bg-secondarycolor/10'
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, -10, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 25,
                ease: 'easeInOut',
              }}
            />

            {/* Small floating elements */}
            {/* {[...Array(6)].map((_, i) => (
              <motion.div
                key={`Small-floating-${i}`}
                className={`absolute w-4 h-4 rounded-full ${i % 2 === 0 ? 'bg-primarycolor/20' : 'bg-secondarycolor/20'}`}
                style={{
                  top: `${20 + i * 15}%`,
                  left: `${10 + i * 15}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  x: [0, 10, 0],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  duration: 5 + i,
                  ease: 'easeInOut',
                  delay: i * 0.5,
                }}
              />
            ))} */}
          </div>

          <div className='container mx-auto px-4 z-10 py-20'>
            <div className='max-w-7xl mx-auto'>
              {/* Section title with animated underline */}
              <motion.div
                className='text-center mb-16'
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h1 className='text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-primarycolor to-secondarycolor bg-clip-text text-transparent'>
                  {t('aboutUs')}
                  {/* <span className='text-gray-400 dark:text-gray-500'>
                    (关于我们)
                  </span> */}
                </h1>
                <motion.div
                  className='h-1 w-24 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto'
                  initial={{ width: 0 }}
                  animate={{ width: 96 }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </motion.div>

              {/* Content cards with improved layout */}
              <motion.div
                className='grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12'
                initial='hidden'
                animate='visible'
                variants={staggerContainer}
              >
                {/* Left card - Main description */}
                <motion.div
                  variants={fadeInLeft}
                  className='relative p-8 rounded-3xl shadow-xl bg-white dark:bg-gray-900 backdrop-blur-sm bg-opacity-90 dark:bg-opacity-90 border border-gray-200 dark:border-gray-800 h-full flex flex-col justify-center group'
                >
                  {/* Decorative corner accent */}
                  <div className='absolute top-0 left-0 w-20 h-20 overflow-hidden'>
                    <div className='absolute top-0 left-0 w-20 h-20 bg-primarycolor/10 dark:bg-primarycolor/20 transform -translate-x-1/2 -translate-y-1/2 rotate-45'></div>
                  </div>

                  <div className='relative z-10'>
                    {/* <h2 className='text-2xl md:text-3xl font-bold mb-6 text-gray-900 dark:text-white'>
                    Our Mission
                  </h2> */}
                    <div>{t('atXSpace_1')}</div>
                    <div>{t('atXSpace_2')}</div>

                    {/* Animated button */}
                    <motion.button
                      className='mt-8 px-6 py-3 bg-gradient-to-r bg-primarycolor dark:bg-secondarycolor text-white dark:text-black rounded-full font-medium flex items-center group-hover:shadow-lg transition-shadow'
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={scrollToVissionSection}
                    >
                      {t('learnMore')}
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='h-5 w-5 ml-2'
                        viewBox='0 0 20 20'
                        fill='currentColor'
                      >
                        <path
                          fillRule='evenodd'
                          d='M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </motion.button>
                  </div>
                </motion.div>

                {/* Right card - Welcome message */}
                <motion.div
                  variants={fadeInRight}
                  className='relative p-8 rounded-3xl shadow-xl bg-white dark:bg-gray-900 backdrop-blur-sm bg-opacity-90 dark:bg-opacity-90 border border-gray-200 dark:border-gray-800 h-full flex flex-col justify-center overflow-hidden'
                >
                  {/* Decorative corner accent */}
                  <div className='absolute top-0 right-0 w-20 h-20 overflow-hidden'>
                    <div className='absolute top-0 right-0 w-20 h-20 bg-secondarycolor/10 dark:bg-secondarycolor/20 transform translate-x-1/2 -translate-y-1/2 rotate-45'></div>
                  </div>

                  {/* Decorative floating elements */}
                  <motion.div
                    className='absolute -bottom-4 -right-4 w-32 h-32 rounded-full bg-gradient-to-r from-primarycolor/5 to-secondarycolor/5 dark:from-primarycolor/10 dark:to-secondarycolor/10'
                    animate={{
                      scale: [1, 1.2, 1],
                      rotate: [0, 45, 0],
                    }}
                    transition={{
                      repeat: Number.POSITIVE_INFINITY,
                      duration: 15,
                      ease: 'easeInOut',
                    }}
                  />

                  <div className='relative z-10'>
                    {/* <h2 className='text-2xl md:text-3xl font-bold mb-6 text-gray-900 dark:text-white'>
                    Welcome
                  </h2> */}
                    <div>{t('welcometoXSpace')}</div>
                    <div>{t('lookingToAcquire')}</div>

                    {/* Stats */}
                    <div className='mt-8 grid grid-cols-2 gap-4'>
                      <div className='bg-gray-50 dark:bg-gray-800 p-4 rounded-xl'>
                        <div className='text-3xl font-bold text-primarycolor dark:text-secondarycolor'>
                          9+
                        </div>
                        <div className='text-sm text-gray-600 dark:text-gray-400'>
                          {t('yearsExperience')}
                        </div>
                      </div>
                      <div className='bg-gray-50 dark:bg-gray-800 p-4 rounded-xl'>
                        <div className='text-3xl font-bold text-primarycolor dark:text-secondarycolor'>
                          500B+
                        </div>
                        <div className='text-sm text-gray-600 dark:text-gray-400'>
                          AED {t('transactions')}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Scroll indicator */}
              {/* <motion.div
              className='flex justify-center mt-16'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              <motion.div
                className='w-6 h-10 border-2 border-gray-400 dark:border-gray-600 rounded-full flex justify-center'
                animate={{ y: [0, 10, 0] }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  duration: 1.5,
                  ease: 'easeInOut',
                }}
              >
                <motion.div
                  className='w-1.5 h-1.5 bg-primarycolor dark:bg-secondarycolor rounded-full mt-2'
                  animate={{
                    y: [0, 4, 0],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    repeat: Number.POSITIVE_INFINITY,
                    duration: 1.5,
                    ease: 'easeInOut',
                  }}
                />
              </motion.div>
            </motion.div> */}
            </div>
          </div>
        </section>

        {/* Company Profile Section */}
        <section className='py-20 px-4'>
          <div className='container mx-auto'>
            <motion.div
              className='flex flex-col lg:flex-row items-center gap-12 lg:gap-20'
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true, margin: '-100px' }}
              variants={staggerContainer}
            >
              <motion.div
                variants={fadeInLeft}
                className='relative w-full lg:w-2/5 h-[30rem] rounded-3xl overflow-hidden'
              >
                <Image
                  className='object-cover rounded-3xl shadow-xl transition-transform duration-700 hover:scale-105'
                  src='https://images.x-space.ae/assets/images/Aboutus pic/Company.webp'
                  alt='Company Profile'
                  fill
                  sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                  priority
                  unoptimized
                />

                {/* Decorative elements */}
                <div className='absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-primarycolor/20 dark:bg-primarycolor/30 z-0'></div>
                <div className='absolute -top-6 -right-6 w-24 h-24 rounded-full bg-secondarycolor/20 dark:bg-secondarycolor/30 z-0'></div>
              </motion.div>

              <motion.div
                variants={fadeInRight}
                className='w-full lg:w-3/5 space-y-8'
              >
                <h2 className='sr-only'>
                  X-Space Real Estate Broker
                </h2>

                <h3 className='text-3xl md:text-4xl font-bold text-gray-900 dark:text-white'>
                  {t('companyName')}
                </h3>

                <motion.div
                  variants={fadeInUp}
                  className='text-gray-700 dark:text-gray-300 text-lg leading-relaxed'
                >
                  <span className='font-semibold text-primarycolor dark:text-secondarycolor'></span>{' '}
                  <div>{t('company_detail_1')}</div>
                </motion.div>

                <motion.div
                  variants={fadeInUp}
                  className='text-gray-700 dark:text-gray-300 text-lg leading-relaxed'
                >
                  {t('company_detail_2')}
                </motion.div>

                <motion.div
                  variants={fadeInUp}
                  className='text-gray-700 dark:text-gray-300 text-lg leading-relaxed'
                >
                  {t('company_detail_3')}
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Founders Section */}
        {/* <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900 relative overflow-hidden'>
          <div className='absolute inset-0 luxury-pattern opacity-5 dark:opacity-10'></div>

          <div className='container mx-auto relative z-10'>
            <motion.div
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true }}
              variants={scaleIn}
              className='text-center mb-16'
            >
              <h2 className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'>
                Our Visionary Leaders
              </h2>
              <div className='w-24 h-1 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mx-auto mb-6'></div>
              <p className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
                Meet the innovative minds behind X-Space Real Estate who have
                transformed the Dubai property landscape.
              </p>
            </motion.div>

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-16'>
              <motion.div
                initial='hidden'
                whileInView='visible'
                viewport={{ once: true }}
                variants={fadeInLeft}
                className='flex flex-col items-center'
              >
                <div className='relative w-64 h-80 mb-8'>
                  <div className='absolute -inset-4 border-2 border-[#D4AF37] rounded-3xl transform rotate-6 z-0'></div>

                  <div className='absolute inset-0 rounded-3xl bg-gradient-to-r from-primarycolor to-secondarycolor opacity-20 blur-lg transform -rotate-6'></div>

                  <div className='relative w-full h-full rounded-3xl overflow-hidden border-4 border-white dark:border-gray-800 shadow-xl'>
                    <Image
                      src='https://images.x-space.ae/assets/images/Aboutus pic/Jason.jpg'
                      alt='Jason Jiang'
                      fill
                      className='object-cover'
                      sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                    />

                    <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent'></div>
                  </div>

                  <div className='absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-primarycolor/20 dark:bg-primarycolor/30 z-0'></div>

                  <div className='absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37] z-10'></div>
                </div>

                <div className='text-center max-w-md'>
                  <h3 className='text-3xl font-bold text-gray-900 dark:text-white mb-2'>
                    Jason Jiang
                  </h3>
                  <div className='w-16 h-1 bg-[#D4AF37] mx-auto mb-4'></div>
                  <h4 className='text-xl text-[#D4AF37] font-medium mb-6'>
                    Co-Founder & Managing Director
                  </h4>
                  <p className='text-gray-700 dark:text-gray-300 text-lg leading-relaxed'>
                    The visionary founder of X-Space Real Estate. In 2017, he
                    established the foundation for the company, setting it on a
                    path to success and innovation in the Dubai real estate
                    industry.
                  </p>

                  <div className='flex justify-center mt-6 space-x-4'>
                    <a
                      href='#'
                      className='w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md hover:bg-[#D4AF37] hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z' />
                      </svg>
                    </a>
                    <a
                      href='#'
                      className='w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md hover:bg-[#D4AF37] hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z' />
                      </svg>
                    </a>
                    <a
                      href='#'
                      className='w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md hover:bg-[#D4AF37] hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z' />
                      </svg>
                    </a>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial='hidden'
                whileInView='visible'
                viewport={{ once: true }}
                variants={fadeInRight}
                className='flex flex-col items-center'
              >
                <div className='relative w-64 h-80 mb-8'>
                  <div className='absolute -inset-4 border-2 border-[#D4AF37] rounded-3xl transform -rotate-6 z-0'></div>

                  <div className='absolute inset-0 rounded-3xl bg-gradient-to-r from-secondarycolor to-primarycolor opacity-20 blur-lg transform rotate-6'></div>

                  <div className='relative w-full h-full rounded-3xl overflow-hidden border-4 border-white dark:border-gray-800 shadow-xl'>
                    <Image
                      src='https://images.x-space.ae/assets/images/Aboutus pic/Kai.jpg'
                      alt='Kay Lu'
                      fill
                      className='object-cover'
                      sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                    />

                    <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent'></div>
                  </div>

                  <div className='absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-secondarycolor/20 dark:bg-secondarycolor/30 z-0'></div>

                  <div className='absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-[#D4AF37] z-10'></div>
                </div>

                <div className='text-center max-w-md'>
                  <h3 className='text-3xl font-bold text-gray-900 dark:text-white mb-2'>
                    Kay Lu
                  </h3>
                  <div className='w-16 h-1 bg-[#D4AF37] mx-auto mb-4'></div>
                  <h4 className='text-xl text-[#D4AF37] font-medium mb-6'>
                    Co-Founder & Managing Director
                  </h4>
                  <p className='text-gray-700 dark:text-gray-300 text-lg leading-relaxed'>
                    The co-founder of X-Space Real Estate. Through his visionary
                    leadership, X-Space has established itself as a leading
                    brokerage firm in both the Chinese and international
                    markets.
                  </p>

                  <div className='flex justify-center mt-6 space-x-4'>
                    <a
                      href='#'
                      className='w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md hover:bg-[#D4AF37] hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z' />
                      </svg>
                    </a>
                    <a
                      href='#'
                      className='w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md hover:bg-[#D4AF37] hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z' />
                      </svg>
                    </a>
                    <a
                      href='#'
                      className='w-10 h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-md hover:bg-[#D4AF37] hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='20'
                        height='20'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z' />
                      </svg>
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section> */}

        <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900 relative overflow-hidden'>
          <div className='container mx-auto'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className='text-center mb-16'
            >
              <h3 className='text-4xl font-bold mb-4 bg-gradient-to-r from-primarycolor to-secondarycolor bg-clip-text text-transparent'>
                {t('our_visionary_leaders')}
              </h3>
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: 120 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.3 }}
                className='h-1 w-24 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'
              />
              {t('our_visionary_description')}
            </motion.div>

            <div className='space-y-20'>
              {/* First Leader - Image Left, Content Right */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='grid grid-cols-1 lg:grid-cols-2 gap-6 items-center'
              >
                {/* Image */}
                <div className='flex justify-center lg:justify-center'>
                  <div className='relative w-64 h-80 mb-8 lg:mb-0'>
                    <div className='absolute inset-0 rounded-3xl bg-gradient-to-r from-primarycolor to-secondarycolor opacity-20 blur-lg transform -rotate-6'></div>

                    <div
                      className='relative w-full h-full border-4 border-white dark:border-gray-800 shadow-xl overflow-hidden rounded-2xl
              before:absolute before:inset-0 before:bg-gradient-to-r before:from-primarycolor/10 before:to-transparent before:opacity-0
              before:transition-all before:duration-700 hover:before:opacity-60
              after:absolute after:h-[30rem] after:w-4 after:-translate-x-44
              after:translate-y-40 after:-rotate-45 after:bg-white/35 after:opacity-0 
              after:transition-all after:duration-700 after:ease-in-out
              hover:after:translate-x-20 hover:after:-translate-y-80 hover:after:opacity-100
              hover:scale-105 transition-all duration-500'
                    >
                      <Image
                        src='https://images.x-space.ae/assets/images/Aboutus pic/Jason.jpg?tr=w-500,h-628,fo-auto'
                        alt='Jason Jiang'
                        fill
                        className='object-cover'
                        sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                        priority
                        unoptimized
                      />
                      <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent'></div>
                    </div>

                    <div className='absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37] z-10'></div>
                  </div>
                </div>

                {/* Content */}
                <div className='space-y-6 lg:pl-4'>
                  <h3 className='text-3xl font-bold text-gray-900 dark:text-white'>
                    Jason Jiang
                  </h3>
                  <p className='text-xl text-primarycolor dark:text-secondarycolor font-medium'>
                    Co-Founder & CEO
                  </p>
                  <p className='text-gray-700 dark:text-gray-300 leading-relaxed'>
                    With over 15 years of experience in Dubai's real estate
                    market, Jason has established himself as a visionary leader
                    in the industry. His deep understanding of market trends and
                    commitment to innovation has positioned X-SPACE as a pioneer
                    in the real estate sector.
                  </p>
                  <p className='text-gray-700 dark:text-gray-300 leading-relaxed'>
                    Jason's philosophy centers around creating value for clients
                    through transparency, integrity, and exceptional service.
                    Under his leadership, X-SPACE has grown from a small startup
                    to one of Dubai's most respected real estate brokerages.
                  </p>

                  {/* Social Media Links */}
                  <div className='flex space-x-4 pt-4'>
                    <motion.a
                      href='https://www.facebook.com/xspacerealestate'
                      whileHover={{ y: -5 }}
                      aria-label='Facebook'
                      className='w-10 h-10 ml-4 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='18'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z' />
                      </svg>
                    </motion.a>
                    <motion.a
                      href='https://ae.linkedin.com/company/xspacerealestate?trk=public_post_feed-actor-image'
                      whileHover={{ y: -5 }}
                      aria-label='LinkedIn'
                      className='w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='18'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z' />
                      </svg>
                    </motion.a>
                    <motion.a
                      href='https://www.instagram.com/xspace_realestate'
                      whileHover={{ y: -5 }}
                      aria-label='Instagram'
                      className='w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='18'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z' />
                      </svg>
                    </motion.a>
                  </div>
                </div>
              </motion.div>

              {/* Second Leader - Content Left, Image Right */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className='grid grid-cols-1 lg:grid-cols-2 gap-6 items-center'
              >
                {/* Content */}
                <div className='space-y-6 order-2 lg:order-1 lg:px-8 lg:text-right'>
                  <h3 className='text-3xl font-bold text-gray-900 dark:text-white'>
                    Kay Lu
                  </h3>
                  <p className='text-xl text-primarycolor dark:text-secondarycolor font-medium'>
                    Co-Founder & CEO
                  </p>
                  <p className='text-gray-700 dark:text-gray-300 leading-relaxed'>
                    Kay Lu brings a wealth of international experience to
                    X-SPACE, having worked in real estate markets across Asia,
                    Europe, and the Middle East. His global perspective and
                    strategic vision have been instrumental in expanding the
                    company's international client base.
                  </p>
                  <p className='text-gray-700 dark:text-gray-300 leading-relaxed'>
                    With a background in architecture and business
                    administration, Kay Lu oversees the operational aspects of
                    X-SPACE, ensuring that every client interaction exceeds
                    expectations. His dedication to excellence has established
                    new standards in the industry.
                  </p>

                  {/* Social Media Links */}
                  <div className='flex space-x-4 pt-4 mr-4'>
                    <motion.a
                      href='https://www.facebook.com/xspacerealestate'
                      whileHover={{ y: -5 }}
                      aria-label='Facebook'
                      className='w-10 h-10 ml-4 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='18'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z' />
                      </svg>
                    </motion.a>
                    <motion.a
                      href='https://ae.linkedin.com/company/xspacerealestate?trk=public_post_feed-actor-image'
                      whileHover={{ y: -5 }}
                      aria-label='LinkedIn'
                      className='w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='18'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z' />
                      </svg>
                    </motion.a>
                    <motion.a
                      href='https://www.instagram.com/xspace_realestate'
                      whileHover={{ y: -5 }}
                      aria-label='Instagram'
                      className='w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='18'
                        fill='currentColor'
                        viewBox='0 0 16 16'
                      >
                        <path d='M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z' />
                      </svg>
                    </motion.a>
                  </div>
                </div>

                {/* Image */}
                <div className='flex justify-center lg:justify-center order-1 lg:order-2'>
                  <div className='relative w-64 h-80 mb-8 lg:mb-0'>
                    <div className='absolute inset-0 rounded-3xl bg-gradient-to-r from-primarycolor to-secondarycolor opacity-20 blur-lg transform rotate-6'></div>

                    <div
                      className='relative w-full h-full border-4 border-white dark:border-gray-800 shadow-xl
              overflow-hidden rounded-2xl
              before:absolute before:inset-0 before:bg-gradient-to-r before:from-primarycolor/10 before:to-transparent before:opacity-0
              before:transition-all before:duration-700 hover:before:opacity-60
              after:absolute after:h-[30rem] after:w-4 after:-translate-x-44
              after:translate-y-40 after:-rotate-45 after:bg-white/35 after:opacity-0 
              after:transition-all after:duration-700 after:ease-in-out
              hover:after:translate-x-20 hover:after:-translate-y-80 hover:after:opacity-100
              hover:scale-105 transition-all duration-500'
                    >
                      <Image
                        src='https://images.x-space.ae/assets/images/Aboutus pic/Kai.jpg?tr=w-500,h-628,fo-auto'
                        alt='Kay Lu'
                        fill
                        className='object-cover'
                        sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                        priority
                        unoptimized
                      />

                      <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent'></div>
                    </div>

                    <div className='absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-[#D4AF37] z-10'></div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className='py-20 px-4'>
          <div className='container mx-auto'>
            <motion.div
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true }}
              variants={scaleIn}
              className='text-center mb-16'
            >
              <h4 className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'>
                {t('our_values')}
              </h4>
              <div className='w-24 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'></div>
              <div>{t('our_value_1')}</div>
            </motion.div>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {[
                {
                  title: t('integrity'),
                  description: t('integrity_description'),
                },
                {
                  title: t('excellence'),
                  description: t('excellence_description'),
                },
                {
                  title: t('innovation'),
                  description: t('innovation_description'),
                },
                {
                  title: t('respect'),
                  description: t('respect_description'),
                },
                {
                  title: t('collaboration'),
                  description: t('collaboration_description'),
                },
                {
                  title: t('responsibility'),
                  description: t('responsibility_description'),
                },
              ].map((value, index) => (
                <motion.div
                  key={`Values-Section-${index}`}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: true }}
                  variants={{
                    hidden: { opacity: 0, y: 50 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        delay: index * 0.1,
                      },
                    },
                  }}
                  className='bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 hover:shadow-xl transition-shadow'
                >
                  <div className='w-12 h-12 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mb-6 mx-auto'>
                    <span className='text-2xl font-bold text-primarycolor dark:text-secondarycolor'>
                      {index + 1}
                    </span>
                  </div>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-4 text-center'>
                    {value.title}
                  </h3>

                  <div className='text-gray-700 dark:text-gray-300 text-center'>
                    {value.description}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900'>
          <div className='container mx-auto'>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='text-center mb-16'
            >
              <h4 className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'>
                {t('our_dynamic_team')}
              </h4>
              <div className='w-24 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'></div>
              {t('our_dynamic_description')}
            </motion.div>

            <div className='flex flex-col lg:flex-row items-center justify-between gap-8 mb-10'>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className='w-full lg:w-auto'
              >
                <ul className='flex lg:flex-col flex-row flex-wrap justify-center items-center bg-white dark:bg-gray-800 rounded-2xl shadow-md p-2 lg:p-4'>
                  {uniqueLanguages.map((language) => (
                    <li
                      key={language}
                      className={`cursor-pointer py-2 px-4 lg:px-6 m-1 transition-all duration-300 ${
                        selectedLanguage === language
                          ? 'font-semibold bg-primarycolor dark:bg-secondarycolor text-white dark:text-black rounded-xl shadow-md'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl'
                      }`}
                      onClick={() => handleLanguageClick(language)}
                    >
                      {language}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className='w-full lg:w-5/6 overflow-hidden'
              >
                <div className='slider-container'>
                  {isLoading ? (
                    <div className='text-center py-20'>
                      Loading Agent Details...
                    </div>
                  ) : (
                    <Slider {...dynamicSettings}>
                      {filteredAgents.map((teamMember) => {
                        const memberSlug = teamMember.name
                          .toLowerCase()
                          .replace(/\s+/g, '-');

                        return (
                          <div
                            key={teamMember._id}
                            className='px-3 py-10 flex justify-center'
                          >
                            <Link href={`/agents/${memberSlug}`} rel='preload'>
                              <motion.div
                                whileHover={{
                                  y: -10,
                                  transition: { duration: 0.3 },
                                }}
                                className='relative flex flex-col items-center group w-full 
                    max-w-[300px] sm:max-w-[260px] lg:max-w-[280px] mx-auto'
                              >
                                <div
                                  className='w-full aspect-[3/4] relative rounded-2xl overflow-hidden shadow-lg
                      before:absolute before:inset-0 before:bg-gradient-to-r before:from-primarycolor/10 before:to-transparent before:opacity-0
                      before:transition-all before:duration-700 hover:before:opacity-60
                      after:absolute after:h-[30rem] after:w-4 after:-translate-x-44
                      after:translate-y-40 after:-rotate-45 after:bg-white/35 after:opacity-0 
                      after:transition-all after:duration-700 after:ease-in-out
                      hover:after:translate-x-20 hover:after:-translate-y-80 hover:after:opacity-100
                      hover:scale-105 transition-all duration-500'
                                >
                                  <Image
                                    style={{
                                      filter: loading ? 'blur(20px)' : 'none',
                                      transition: '1s filter linear',
                                      objectFit: 'cover',
                                    }}
                                    fill
                                    onLoad={() => setLoading(false)}
                                    className='rounded-2xl group-hover:scale-110 transition-transform duration-500'
                                    src={
                                      teamMember.profile_image ||
                                      '/placeholder.svg'
                                    }
                                    alt={teamMember.name}
                                    sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                                    unoptimized
                                  />
                                  <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent'></div>
                                  <div className='absolute bottom-0 left-0 right-0 p-4 text-center'>
                                    <h3 className='text-white text-xl font-bold mb-1'>
                                      {teamMember.name}
                                    </h3>
                                    <p className='text-white/80 text-sm mb-2'>
                                      {teamMember.designation}
                                    </p>
                                    <span className='text-sm text-primarycolor dark:text-secondarycolor font-medium flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity'>
                                      View Profile
                                      <ChevronRight
                                        size={16}
                                        className='ml-1'
                                      />
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                            </Link>
                          </div>
                        );
                      })}
                    </Slider>
                  )}

                  {/* ✅ Custom Mobile scroll indicator */}
                  {/* <div className='text-center relative mt-4 block sm:hidden'> */}
                  <div className='text-center relative mt-4'>
                    <span className='flex items-center justify-center text-sm text-gray-500 dark:text-gray-400'>
                      <ChevronLeft className='w-4 h-4 mr-1' />
                      {t('scroll_to_explore_agents')}
                      <ChevronRight className='w-4 h-4 ml-1' />
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className='py-20 px-4'>
          <div className='container mx-auto'>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='text-center mb-16'
            >
              <h4 className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'>
                {t('values')}
              </h4>
              <div className='w-24 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'></div>
            </motion.div>

            <div className='flex flex-col-reverse lg:flex-row justify-between items-center gap-16'>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, staggerChildren: 0.2 }}
                viewport={{ once: true }}
                className='w-full lg:w-3/5 space-y-8'
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify'
                >
                  {t('values_description_1')}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify'
                >
                  {t('values_description_2')}
                </motion.div>

                {/* <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                  className='p-6 bg-gray-50 dark:bg-gray-800 rounded-2xl border-l-4 border-primarycolor dark:border-secondarycolor'
                >
                  <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed italic'>
                    "What we value determines what we do."
                  </p>
                  <p className='text-right text-gray-600 dark:text-gray-400 mt-2'>
                    — Craig Groeschel
                  </p>
                  <p className='text-gray-700 dark:text-gray-300 mt-4'>
                    This quote encapsulates our belief that our core values
                    shape our actions and drive us to consistently meet and
                    exceed our clients' expectations.
                  </p>
                </motion.div> */}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className='w-full lg:w-2/5 relative'
              >
                <div className='relative rounded-3xl overflow-hidden shadow-2xl'>
                  <div className='absolute inset-0 bg-gradient-to-br from-primarycolor/20 to-secondarycolor/20 z-10'></div>
                  <Image
                    src='https://images.x-space.ae/assets/images/Aboutus pic/value.png'
                    alt='Our Values'
                    width={600}
                    height={800}
                    priority
                    className='w-full h-auto object-cover rounded-3xl hover:scale-105 transition-transform duration-700'
                    unoptimized
                  />
                </div>
                <div className='absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-primarycolor/20 dark:bg-primarycolor/30 z-0'></div>
                <div className='absolute -top-6 -right-6 w-24 h-24 rounded-full bg-secondarycolor/20 dark:bg-secondarycolor/30 z-0'></div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Vision & Mission Section */}
        <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900'>
          <div className='container mx-auto'>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='text-center mb-16'
              ref={visionSectionRef}
            >
              <h4 className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'>
                {t('vision_and_mission')}
              </h4>
              <div className='w-24 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'></div>
            </motion.div>

            <div className='grid grid-cols-1 lg:grid-cols-3 gap-12 items-center'>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className='relative'
              >
                <div className='relative rounded-3xl overflow-hidden shadow-2xl'>
                  <div className='absolute inset-0 bg-gradient-to-br from-primarycolor/20 to-secondarycolor/20 z-10'></div>
                  <Image
                    src='https://images.x-space.ae/assets/images/Aboutus pic/vision&mission.png'
                    alt='Vision and Mission'
                    width={600}
                    height={800}
                    priority
                    className='w-full h-auto object-cover rounded-3xl hover:scale-105 transition-transform duration-700'
                    unoptimized
                  />
                </div>
                <div className='absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-primarycolor/20 dark:bg-primarycolor/30 z-0'></div>
                <div className='absolute -top-6 -left-6 w-24 h-24 rounded-full bg-secondarycolor/20 dark:bg-secondarycolor/30 z-0'></div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className='bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl'
              >
                <div className='flex items-center mb-6'>
                  <div className='w-12 h-12 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mr-4 ml-3'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-6 w-6 text-primarycolor'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M13 10V3L4 14h7v7l9-11h-7z'
                      />
                    </svg>
                  </div>
                  <h3 className='text-3xl font-bold text-gray-900 dark:text-white'>
                    {t('vision')}
                  </h3>
                </div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'
                >
                  {t('vision_description')}
                </motion.div>

                {/* <motion.p
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed'
                >
                  As we grow, we remain steadfast in upholding these principles
                  that have been instrumental in our company's success and in
                  serving our valued clients over time.
                </motion.p> */}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className='bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl'
              >
                <div className='flex items-center mb-6'>
                  <div className='w-12 h-12 rounded-full bg-secondarycolor/10 dark:bg-secondarycolor/20 flex items-center justify-center mr-4 ml-3'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-6 w-6 text-secondarycolor'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                      />
                    </svg>
                  </div>
                  <h3 className='text-3xl font-bold text-gray-900 dark:text-white'>
                    {t('mission')}
                  </h3>
                </div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify'
                >
                  {t('mission_description')}
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className='py-20 px-4'>
          <div className='container mx-auto'>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='text-center mb-16'
            >
              <h4 className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'>
                {t('visit_our_office')}
              </h4>
              <div className='w-24 h-1 bg-gradient-to-r from-primarycolor to-secondarycolor mx-auto mb-6'></div>
              {t('visit_our_office_description')}
            </motion.div>

            <div className='relative'>
              {/* Decorative elements */}
              <div className='absolute -top-6 -left-6 w-24 h-24 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 z-0'></div>
              <div className='absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-secondarycolor/10 dark:bg-secondarycolor/20 z-0'></div>

              {/* Gold corner accents */}
              <div className='absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37] z-10'></div>
              <div className='absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 border-[#D4AF37] z-10'></div>
              <div className='absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-[#D4AF37] z-10'></div>
              <div className='absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37] z-10'></div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className='relative rounded-3xl overflow-hidden shadow-2xl mx-auto max-w-4xl'
              >
                <div className='absolute inset-0 bg-gradient-to-br from-primarycolor/5 to-secondarycolor/5 z-10 pointer-events-none'></div>
                <Map />
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className='mt-12 text-center'
            >
              <div className='inline-flex items-center justify-center bg-white dark:bg-gray-800 px-6 py-4 rounded-full shadow-md'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-6 w-6 text-primarycolor mr-3'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                  />
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                  />
                </svg>
                <span className='text-gray-800 dark:text-gray-200 font-medium'>
                  X-Space Real Estate, Dubai, United Arab Emirates
                </span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Call to Action Section - Redesigned without gradient background */}
        <section className='py-20 px-4 bg-white dark:bg-gray-950'>
          <div className='container mx-auto'>
            <div className='relative overflow-hidden rounded-3xl bg-gray-50 dark:bg-gray-900 shadow-xl'>
              {/* Luxury pattern background */}
              <div className='absolute inset-0 luxury-pattern opacity-5 dark:opacity-10'></div>

              {/* Gold corner accents */}
              <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-[#D4AF37] rounded-tl-3xl z-10'></div>
              <div className='absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-[#D4AF37] rounded-tr-3xl z-10'></div>
              <div className='absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-[#D4AF37] rounded-bl-3xl z-10'></div>
              <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-[#D4AF37] rounded-br-3xl z-10'></div>

              <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 md:p-12 relative z-10'>
                {/* Left column - Image */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='relative rounded-2xl overflow-hidden h-[400px] lg:h-full'
                >
                  <Image
                    // src='https://images.x-space.ae/assets/images/Aboutus pic/Company.webp'
                    src='https://images.x-space.ae/assets/images/Aboutus pic/Company.webp?width=720'
                    alt='Luxury Dubai Real Estate'
                    fill
                    className='object-cover'
                    priority
                    sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
                    unoptimized
                  />
                  <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent'></div>

                  {/* Overlay content */}
                  <div className='absolute bottom-0 left-0 right-0 p-6 text-white'>
                    <h3 className='text-2xl font-bold mb-2'>
                      Exceptional Properties
                    </h3>
                    <p className='text-white/80'>
                      Discover Dubai's most prestigious addresses
                    </p>
                  </div>
                </motion.div>

                {/* Right column - Content */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className='flex flex-col justify-center'
                >
                  <div>{t('ready_dream_property')}</div>

                  <div>{t('ready_dream_property_description')}</div>

                  {/* Feature highlights */}
                  <div className='space-y-4 mb-8'>
                    <div className='flex items-start'>
                      <div className='flex-shrink-0 w-10 h-10 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mr-4 ml-3'>
                        <Check className='w-5 h-5 text-primarycolor' />
                      </div>
                      <div>
                        <h4 className='text-lg font-semibold text-gray-900 dark:text-white'>
                          {t('personalized_service')}
                        </h4>
                        <div>{t('personalized_service_description')}</div>
                      </div>
                    </div>

                    <div className='flex items-start'>
                      <div className='flex-shrink-0 w-10 h-10 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mr-4 ml-3'>
                        <Check className='w-5 h-5 text-primarycolor' />
                      </div>
                      <div>
                        <h4 className='text-lg font-semibold text-gray-900 dark:text-white'>
                          {t('market_expertise')}
                        </h4>
                        <div>{t('market_expertise_description')}</div>
                      </div>
                    </div>

                    <div className='flex items-start'>
                      <div className='flex-shrink-0 w-10 h-10 rounded-full bg-primarycolor/10 dark:bg-primarycolor/20 flex items-center justify-center mr-4 ml-3'>
                        <Check className='w-5 h-5 text-primarycolor' />
                      </div>
                      <div>
                        <h4 className='text-lg font-semibold text-gray-900 dark:text-white'>
                          {t('exclusive_listings')}
                        </h4>
                        <div>{t('exclusive_listings_description')}</div>
                      </div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => router.push('/contact-us')}
                    className='self-start px-8 py-4 bg-white dark:bg-gray-800 text-primarycolor dark:text-secondarycolor border-2 border-primarycolor dark:border-secondarycolor rounded-full font-medium hover:bg-primarycolor hover:text-white dark:hover:bg-secondarycolor dark:hover:text-black transition-colors shadow-lg'
                  >
                    {t('contact_us_today')}
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
